import { UserInfo, getResultFromResponse, runAsyncSafe } from '@web3/utils';
import { useMemo } from 'react';
import { queryWalletList } from '../api';
import { WalletData, WalletTypesString } from '../types';
import { EventBasedStore, pickStoreActions } from '../utils/EventBasedStore';
import { dependencyContainer, getRpcUrl } from '../utils/common';
import { cloudWalletChainId } from '../utils/constants';
import { isBrowser } from '../utils/trick';
import { ChainTypes } from '../wallet-hub/core/types';
import { getBybitCloudWalletProvider } from '../wallet-hub/helpers/bybitInBrowser/BybitCloudWalletProvider';
import { userInfoStore } from './userInfo';

export const getChainId = () => {
  if (isBrowser) {
    return Number(sessionStorage.getItem(cloudWalletChainId)) || 1;
  } else {
    return 1;
  }
};
export const setChainId = (chainId: number) => {
  if (isBrowser) {
    sessionStorage.setItem(cloudWalletChainId, String(chainId));
  }
};

type State = {
  loading: boolean;
  isReady: boolean;
  walletList: WalletData[];
  userInfo?: UserInfo;
  chainId: number;
};

const computeState = ({ walletList, userInfo }: State) => {
  const cloudWallet = walletList.find((w) => w.walletType === WalletTypesString.CLOUD);
  const cloudWalletList = cloudWallet?.addressChain || [];
  //  1 是evm 链的类型
  const supportedEvmChainIds = cloudWalletList.filter((w) => w.chainType === 1).map((w) => Number(w.chainId));
  const watcherWallets = walletList.filter((item) => item.walletType === WalletTypesString.FOLLOW_ADDRESS);

  return {
    cloudWalletList,
    cloudWallet,
    hasCloudWallet: Boolean(userInfo && cloudWalletList.length),
    supportedEvmChainIds,
    watcherWallets,
  };
};

type ComputedState = ReturnType<typeof computeState>;

export type UserCloudWalletState = ComputedState & State;

class UserCloudWalletStore extends EventBasedStore<State, ComputedState> {
  constructor() {
    super(
      {
        loading: false,
        isReady: false,
        walletList: [],
        chainId: getChainId(),
      },
      computeState,
    );
  }
  private _refreshDeps = dependencyContainer();
  async refreshWalletList(force = false) {
    let userInfo = userInfoStore.state.userInfo;
    if (!userInfo && !userInfoStore.state.isReady) {
      userInfo = await userInfoStore.getUserInfo();
    }
    const res = this._refreshDeps.checkAndRun(
      async () => {
        if (userInfo) {
          this.setState({
            loading: true,
            // 用户信息变化，清空云钱包地址
            walletList: userInfo.id === this.state.userInfo?.id ? this.state.walletList : [],
            userInfo,
          });
          const [err, res] = await runAsyncSafe(getResultFromResponse(queryWalletList()));
          if (res) {
            this.setState({
              loading: false,
              isReady: true,
              walletList: res.walletDetail,
            });
            return;
          }
        }
        this.setState({
          loading: false,
          isReady: true,
          walletList: [],
          userInfo,
        });
      },
      [userInfo?.id],
      force,
    );
    return res.result;
  }

  /**
   *
   * @param info chainId 或是 chainCode
   * @returns
   */

  getWallet(info: string | number) {
    const chainId = Number(info);
    const wallet = this.state.cloudWalletList.find((c) => (!isNaN(chainId) ? chainId === Number(c.chainId) : info === c.chainCode));
    return wallet;
  }

  getChainCode(info: string | number): string | undefined {
    return this.getWallet(info)?.chainCode;
  }

  getRpcUrl(info: string | number): string {
    const chainCode = this.getChainCode(info);
    if (!chainCode) return '';
    return getRpcUrl(chainCode);
  }

  switchEvmChain(info: string | number) {
    const wallet = this.getWallet(info);
    if (!wallet || wallet.chainType !== ChainTypes.EVM) {
      throw new Error(`cloud wallet not support ${info}`);
    }
    this.setState({
      chainId: Number(wallet.chainId),
    });
    setChainId(Number(wallet.chainId));
  }

  getEvmProvider() {
    const wallet = this.getWallet(this.state.chainId);
    if (!wallet) {
      return;
    }
    return getBybitCloudWalletProvider(wallet.chainCode, wallet.address);
  }
}

/** @deprecated 后续云钱包，不推荐使用这个了 */
export const userCloudWalletStore = new UserCloudWalletStore();

/** @deprecated 后续云钱包，不推荐使用这个了 */
export const useCloudWallet = () => {
  const state = userCloudWalletStore.useStoreState();
  const actions = useMemo(() => pickStoreActions(userCloudWalletStore, ['refreshWalletList', 'getWallet', 'getChainCode', 'getRpcUrl']), []);
  return {
    ...state,
    ...actions,
  };
};
